import { useEffect, useState, type JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { DeactivateBlocked } from '@/types/sites';

/**
 * Plan 09-02 D-88 DeactivateSiteConfirmDialog — two-state UX (mirrors the
 * Phase 8 ResetPasswordDialog two-state pattern):
 *
 *  - **Default state:** asks "Are you sure you want to deactivate this site?".
 *  - **Blocked state:** entered when the parent passes a non-null `blocked`
 *    prop (the backend returned 400 with `requiresOverride: true` plus the
 *    actual counts). Surfaces the counts + an explicit override checkbox.
 *    The Confirm button reads "Force Deactivate" and is disabled until the
 *    override is checked.
 */
export interface DeactivateSiteConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly siteName: string;
  readonly blocked: DeactivateBlocked | null;
  readonly onConfirm: (force: boolean) => void;
  readonly onCancel: () => void;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
}

export function DeactivateSiteConfirmDialog({
  open,
  onOpenChange,
  siteName,
  blocked,
  onConfirm,
  onCancel,
  submitting = false,
  errorMessage,
}: DeactivateSiteConfirmDialogProps): JSX.Element {
  const [overrideChecked, setOverrideChecked] = useState<boolean>(false);

  // Reset the override toggle whenever the dialog re-opens or transitions
  // between default <-> blocked.
  useEffect(() => {
    if (!open) setOverrideChecked(false);
  }, [open]);

  const isBlocked = blocked !== null;
  const confirmDisabled = submitting || (isBlocked && !overrideChecked);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="deactivate-site-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {siteName}?</AlertDialogTitle>
          <AlertDialogDescription>
            {isBlocked
              ? 'This site has active dependencies. Deactivating it will mark it inactive and prevent new operations against it.'
              : 'The site will be marked inactive and hidden from default lists. You can reactivate it later.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {blocked !== null && (
          <Alert variant="destructive" data-testid="deactivate-site-blocked-alert">
            <AlertTitle>Override required</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5">
                <li data-testid="deactivate-site-active-machines">
                  Active machines: <strong>{blocked.activeMachinesCount}</strong>
                </li>
                <li data-testid="deactivate-site-pending-logsheets">
                  Pending logsheets: <strong>{blocked.pendingLogsheetsCount}</strong>
                </li>
              </ul>
              <div className="mt-3 flex items-center gap-2">
                <Checkbox
                  id="deactivate-site-override"
                  checked={overrideChecked}
                  onCheckedChange={(v) => {
                    setOverrideChecked(v === true);
                  }}
                  data-testid="deactivate-site-override"
                />
                <Label htmlFor="deactivate-site-override" className="text-sm">
                  I understand and want to deactivate anyway
                </Label>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {errorMessage != null && errorMessage !== '' && (
          <Alert variant="destructive" data-testid="deactivate-site-error">
            <AlertTitle>Could not deactivate</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onCancel();
            }}
            disabled={submitting}
            data-testid="deactivate-site-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm(isBlocked);
            }}
            disabled={confirmDisabled}
            data-testid="deactivate-site-confirm"
          >
            {submitting ? 'Deactivating…' : isBlocked ? 'Force Deactivate' : 'Deactivate'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
